import { saveStateWithAjax, getPackageImageSource, checkSubmit, checkKey } from './screen-package-selection-global'
import { openConfigModal, clearStoredChoice } from './screen-config-modal'

/* ============================================================
   SQ6 e-tron performance (Fahrzeug 70 und 71)
============================================================ */

const YFA = 'YFA_Q6etron'
const YFB = 'YFB_Q6etron'
const YN3 = 'YN3_SQ6_perf'
const YN4 = 'YN4_SQ6_perf'
const WHEEL_0UD = '0UD_SQ6_perf'
const WHEEL_0UQ = '0UQ_SQ6_perf'
const WHEELS = [WHEEL_0UD, WHEEL_0UQ]
const YFA_REQUIRED = ['FC1', 'Q8H', '0TF']
const WHEEL_MODAL_GROUP = 'sq6_perf_yfa_wheel'

const RULES_SQ6_PERF = {
  rules: [
    { type: 'disableTargets', ifOn: 'FC1', disable: [YN3, YN4, YFB] },
    { type: 'disableTargets', ifOn: 'Q8H', disable: [YN3, YN4, YFB] },
    { type: 'disableTargets', ifOn: WHEEL_0UD, disable: [WHEEL_0UQ, YN3, YN4, YFB] },
    { type: 'disableTargets', ifOn: WHEEL_0UQ, disable: [WHEEL_0UD, YN3, YN4, YFB] },
    { type: 'disableTargets', ifOn: '0TF', disable: [YN3, YN4, YFB] },
    { type: 'disableTargets', ifOn: YFA, disable: [YN3, YN4, YFB] },

    { type: 'requires', ifOn: YFA, thenOn: YFA_REQUIRED },

    { type: 'excludes', a: WHEEL_0UD, b: WHEEL_0UQ },
    { type: 'excludes', a: YN3, b: YN4 },

    { type: 'allowOnly', ifOn: YN3, allow: [YFB, 'FZ9'] },
    { type: 'allowOnly', ifOn: YN4, allow: [YFB, 'FZ9'] },
    { type: 'allowOnly', ifOn: YFB, allow: [YN3, YN4, 'FZ9'] }
  ]
}

const runtime = {
  engine: null,
  map: null,
  applying: false,
  wheelChoicePending: false
}

/* ============================================================
   DOM HELPERS
============================================================ */

function getLabelFor(mapVal, key) {
  if (mapVal?.tagName === 'LABEL') return mapVal
  if (mapVal?.tagName === 'INPUT') return mapVal.closest('label[data-auicode]')
  return document.querySelector(`label[data-auicode="${key}"]`)
}

function getInputFor(mapVal, key) {
  if (mapVal?.tagName === 'INPUT') return mapVal
  if (mapVal?.tagName === 'LABEL') return mapVal.querySelector('input')
  return document.querySelector(`label[data-auicode="${key}"] input`)
}

function setChecked(map, key, checked) {
  const input = getInputFor(map[key], key)
  const label = getLabelFor(map[key], key)
  if (!input) return

  input.checked = checked
  label?.classList.toggle('is-checked', checked)
}

function setDisabled(map, key, disabled) {
  const input = getInputFor(map[key], key)
  if (input) input.disabled = disabled
}

function setForcedBadge(map, key, forced) {
  const label = getLabelFor(map[key], key)
  label?.classList.toggle('is-forced', forced)
}

function isSelected(map, id) {
  const input = getInputFor(map[id], id)
  const label = getLabelFor(map[id], id)
  if (!input) return false
  return !!input.checked || !!label?.classList?.contains('is-checked')
}

function snapshotState(map) {
  const state = {}
  for (const id of Object.keys(map)) {
    state[id] = isSelected(map, id)
  }
  return state
}

function getSelectedWheel(mapOrSet) {
  if (mapOrSet instanceof Set) {
    return WHEELS.find(id => mapOrSet.has(id)) || null
  }
  return WHEELS.find(id => isSelected(mapOrSet, id)) || null
}

function getPackageLabel(code) {
  const label = document.querySelector(`label[data-auicode="${code}"] .aui-checkbox__label`)
  return label?.textContent?.trim() || code
}

function getConfigModalTranslation(key, fallback) {
  return window.configModalTranslations?.[key] ?? window.modalTranslations?.[key] ?? fallback
}

/* ============================================================
   PERSIST
============================================================ */

const persistQueue = { runId: 0 }

function persistAllStates(map) {
  const allIds = Object.keys(map)
  const priorityIds = [
    YFA,
    YFB,
    YN3,
    YN4,
    'FC1',
    'Q8H',
    WHEEL_0UD,
    WHEEL_0UQ,
    '0TF',
    'FZ9'
  ].filter(id => allIds.includes(id))

  const priorityFalse = priorityIds.filter(id => !isSelected(map, id))
  const priorityTrue = priorityIds.filter(id => isSelected(map, id))
  const rest = allIds.filter(id => !priorityIds.includes(id))
  const orderedIds = [...priorityFalse, ...priorityTrue, ...rest]

  const currentRunId = ++persistQueue.runId
  const delayMs = 35

  function sendNext(index) {
    if (currentRunId !== persistQueue.runId) return
    if (index >= orderedIds.length) return

    const id = orderedIds[index]
    saveStateWithAjax(id, isSelected(map, id))
    setTimeout(() => sendNext(index + 1), delayMs)
  }

  sendNext(0)
}

/* ============================================================
   SUBMIT
============================================================ */

function updateSubmitButton_sq6etron_perf(map) {
  const btn = document.getElementById('navigation-submit-btn')
  if (!btn) return

  if (runtime.wheelChoicePending) {
    btn.disabled = true
    return
  }

  const selected = Object.keys(map).filter(id => isSelected(map, id))
  if (selected.length === 0) {
    btn.disabled = true
    return
  }

  if (isSelected(map, YFB)) {
    btn.disabled = selected.filter(id => id !== YFB).length === 0
    return
  }

  btn.disabled = false
}

/* ============================================================
   ENGINE
============================================================ */

function resolveExclude(a, b, ctx, forced) {
  if (forced.has(a) && !forced.has(b)) return b
  if (forced.has(b) && !forced.has(a)) return a
  if (ctx.lastClicked === a) return b
  if (ctx.lastClicked === b) return a
  return b
}

function dropSelected(selected, forced, id) {
  const removed = selected.delete(id)
  const unforced = forced.delete(id)
  return removed || unforced
}

function computeState(map, config, ctx) {
  const ids = Object.keys(map)
  const selected = new Set(ids.filter(id => isSelected(map, id)))
  const disabled = new Set()
  const forced = new Set()

  let changed = true
  let guard = 0

  while (changed && guard++ < 50) {
    changed = false

    for (const r of (config.rules || [])) {
      if (r.type !== 'disableTargets' || !selected.has(r.ifOn)) continue

      const targets = r.disable || []
      const clickedTarget = ctx.lastClicked
        && ctx.lastClicked !== YFB
        && targets.includes(ctx.lastClicked)
        && selected.has(ctx.lastClicked)

      if (clickedTarget) {
        const ynClickLosesToYfa = selected.has(YFA) && (ctx.lastClicked === YN3 || ctx.lastClicked === YN4)

        if (!ynClickLosesToYfa) {
          if (dropSelected(selected, forced, r.ifOn)) changed = true
          continue
        }
      }

      for (const t of targets) {
        if (!disabled.has(t)) {
          disabled.add(t)
          changed = true
        }
        if (dropSelected(selected, forced, t)) changed = true
      }
    }

    const activeAllowOnly = (config.rules || []).filter(r => r.type === 'allowOnly' && selected.has(r.ifOn))
    if (activeAllowOnly.length) {
      let allowed = new Set(ids)

      for (const r of activeAllowOnly) {
        const thisAllowed = new Set([r.ifOn, ...(r.allow || [])])
        allowed = new Set([...allowed].filter(x => thisAllowed.has(x)))
      }

      for (const id of ids) {
        if (allowed.has(id)) continue

        const yfbClearsYfa = activeAllowOnly.some(r => r.ifOn === YFB)
        if (id === YFA && selected.has(YFA) && !yfbClearsYfa) {
          for (const r of activeAllowOnly) {
            if (dropSelected(selected, forced, r.ifOn)) changed = true
          }
          continue
        }

        if (id === ctx.lastClicked && selected.has(id)) {
          for (const r of activeAllowOnly) {
            const thisAllowed = new Set([r.ifOn, ...(r.allow || [])])
            if (!thisAllowed.has(id) && dropSelected(selected, forced, r.ifOn)) {
              changed = true
            }
          }
          continue
        }

        if (!disabled.has(id)) {
          disabled.add(id)
          changed = true
        }
        if (dropSelected(selected, forced, id)) changed = true
      }
    }

    for (const r of (config.rules || [])) {
      if (r.type !== 'requires' || !selected.has(r.ifOn)) continue

      for (const dep of (r.thenOn || [])) {
        if (disabled.delete(dep)) changed = true
        if (!selected.has(dep)) {
          selected.add(dep)
          changed = true
        }
        if (!forced.has(dep)) {
          forced.add(dep)
          changed = true
        }
      }
    }

    for (const r of (config.rules || [])) {
      if (r.type === 'excludes' && selected.has(r.a) && selected.has(r.b)) {
        const drop = resolveExclude(r.a, r.b, ctx, forced)
        if (dropSelected(selected, forced, drop)) changed = true
      }
    }

    if (selected.has(YFA)) {
      const chosenWheel = getSelectedWheel(selected)
      if (chosenWheel) {
        if (!forced.has(chosenWheel)) {
          forced.add(chosenWheel)
          changed = true
        }
        const otherWheel = WHEELS.find(id => id !== chosenWheel)
        if (!disabled.has(otherWheel)) {
          disabled.add(otherWheel)
          changed = true
        }
        if (dropSelected(selected, forced, otherWheel)) changed = true
      }
    }

    for (const id of [...forced]) {
      if (!selected.has(id) && forced.delete(id)) changed = true
    }

    for (const id of disabled) {
      if (dropSelected(selected, forced, id)) changed = true
    }
  }

  return { selected, disabled, forced }
}

function applyState(map, state) {
  for (const id of Object.keys(map)) {
    setChecked(map, id, state.selected.has(id))
    setDisabled(map, id, state.disabled.has(id) || state.forced.has(id))
    setForcedBadge(map, id, state.forced.has(id))
  }

  getImages_sq6etron_perf(map)
  checkSubmit(map)
  updateSubmitButton_sq6etron_perf(map)
}

function createEngine(config) {
  const ctx = { lastClicked: null }

  function recompute(map, changedId = null) {
    if (changedId) ctx.lastClicked = changedId
    const state = computeState(map, config, ctx)
    applyState(map, state)
    return state
  }

  return { recompute }
}

/* ============================================================
   YFA MODAL (0UD / 0UQ)
============================================================ */

function needsWheelModal(map, changedId, stateBefore) {
  const yfaOn = isSelected(map, YFA)
  const wheelOn = !!getSelectedWheel(map)
  if (!yfaOn || wheelOn) return false

  if (changedId == null) return true
  return changedId === YFA && stateBefore && !stateBefore[YFA]
}

function restoreState(map, state, { clearYfa = false } = {}) {
  for (const id of Object.keys(map)) {
    const checked = clearYfa && id === YFA ? false : !!state[id]
    setChecked(map, id, checked)
  }
}

function openYfaWheelModal(map, stateBefore) {
  runtime.wheelChoicePending = true
  updateSubmitButton_sq6etron_perf(map)
  clearStoredChoice(WHEEL_MODAL_GROUP)

  openConfigModal({
    title: getConfigModalTranslation(
      'followupTitle',
      'Diese Auswahl erfordert weitere Anpassungen Ihrer Konfiguration'
    ),
    groups: [
      {
        id: WHEEL_MODAL_GROUP,
        type: 'choice',
        title: getConfigModalTranslation(
          'followupPackageTitle',
          'Bitte wählen Sie die gewünschte Ausführung'
        ),
        options: WHEELS.map(code => ({
          code,
          label: getPackageLabel(code)
        }))
      }
    ],
    onCancel: () => {
      runtime.wheelChoicePending = false
      runtime.applying = true
      restoreState(map, stateBefore, { clearYfa: true })
      runtime.engine.recompute(map, null)
      persistAllStates(map)
      updateSubmitButton_sq6etron_perf(map)
      runtime.applying = false
    },
    onSubmit: (selections) => {
      const selectedCode = Array.isArray(selections)
        ? selections.find(entry => entry.id === WHEEL_MODAL_GROUP)?.code
        : null

      if (!WHEELS.includes(selectedCode)) return

      runtime.wheelChoicePending = false
      runtime.applying = true
      setChecked(map, selectedCode, true)
      WHEELS.filter(code => code !== selectedCode).forEach(code => setChecked(map, code, false))
      runtime.engine.recompute(map, selectedCode)
      persistAllStates(map)
      updateSubmitButton_sq6etron_perf(map)
      runtime.applying = false
    }
  })
}

/* ============================================================
   LISTENERS
============================================================ */

function addListeners_sq6etron_perf(frontCheckboxesMap, _modalsCheckboxesMap, config = RULES_SQ6_PERF) {
  try {
    if (typeof checkKey === 'function') checkKey(frontCheckboxesMap)
  } catch (e) {
    console.error('[SQ6 perf] checkKey ERROR', e)
  }

  const engine = createEngine(config)
  runtime.engine = engine
  runtime.map = frontCheckboxesMap

  for (const id of Object.keys(frontCheckboxesMap)) {
    const input = getInputFor(frontCheckboxesMap[id], id)
    if (!input) continue

    input.addEventListener('change', function () {
      setTimeout(() => {
        if (runtime.applying) return

        if (runtime.wheelChoicePending) {
          input.checked = !input.checked
          getLabelFor(frontCheckboxesMap[id], id)?.classList.toggle('is-checked', input.checked)
          return
        }

        const stateBefore = snapshotState(frontCheckboxesMap)
        stateBefore[id] = !stateBefore[id]

        const openModal = needsWheelModal(frontCheckboxesMap, id, stateBefore)
        if (openModal) runtime.wheelChoicePending = true

        engine.recompute(frontCheckboxesMap, id)

        if (openModal) {
          openYfaWheelModal(frontCheckboxesMap, stateBefore)
          return
        }

        persistAllStates(frontCheckboxesMap)
        checkSubmit(frontCheckboxesMap)
        updateSubmitButton_sq6etron_perf(frontCheckboxesMap)
      }, 0)
    })
  }

  setTimeout(() => {
    const stateBefore = snapshotState(frontCheckboxesMap)
    const openModal = needsWheelModal(frontCheckboxesMap, null, stateBefore)
    if (openModal) runtime.wheelChoicePending = true

    engine.recompute(frontCheckboxesMap, null)

    if (openModal) {
      openYfaWheelModal(frontCheckboxesMap, stateBefore)
      return
    }

    checkSubmit(frontCheckboxesMap)
    updateSubmitButton_sq6etron_perf(frontCheckboxesMap)
  }, 0)
}

/* ============================================================
   IMAGES
============================================================ */

function getImages_sq6etron_perf(map) {
  for (const id in map) {
    getPackageImages_sq6etron_perf(id, map)
  }
}

function getPackageImages_sq6etron_perf(packageCode, map) {
  const input = getInputFor(map[packageCode], packageCode)
  if (!input) return

  const imageSource = getPackageImageSource(packageCode)
  let img = document.getElementById(packageCode + '-img')

  if (!img && input.checked) {
    if (!imageSource) return
    const wrap = document.querySelector('.image-wrap')
    if (!wrap) return

    const newImg = document.createElement('IMG')
    newImg.classList.add('overlay')
    newImg.src = imageSource
    newImg.id = packageCode + '-img'
    wrap.appendChild(newImg)
    img = newImg
  }

  if (img) img.hidden = !input.checked
}

export {
  addListeners_sq6etron_perf,
  getImages_sq6etron_perf,
  getPackageImages_sq6etron_perf
}
